// jobTimeLogStore — the reactive, SHARDED, spine-layer store for the canonical
// job time log (Job.timeLog / JobTimeLog).
//
// TIME-LOG FOLD (D1). The swim-lane Differentiated Time Log is now the canonical,
// job-owned time log. STORAGE = SHARDED (PO decision): the time log is kept
// PER-JOB — one localStorage entry per job under the key `oxbo-timeline-v8:<jobId>`
// — NOT nested inside `oxbo-wells-v1` (that would write-amplify the wells blob on
// every time-log edit). Canonically it is `Job.timeLog`; physically it is this
// per-job shard.
//
// This module makes each shard a SUBSCRIBABLE source, mirroring
// features/wells/store/spineStore.ts: a module-level store per jobId +
// `useSyncExternalStore` semantics, so a write in one component re-renders every
// component that reads the same job's time log. Each store caches a STABLE
// reference while the underlying localStorage string is unchanged (required by
// useSyncExternalStore — a fresh object each call would loop) and re-reads
// localStorage on every call so a direct `localStorage.setItem` (a test seed, the
// prebake importer, or a cross-tab write) is picked up.
//
// KEY IS UNCHANGED (`oxbo-timeline-v8:<jobId>`) so pre-fold demo/prebake data loads
// without migration (Charter Absolute #7 — no silent destruction). The key builder
// is encapsulated here so a future rename is a one-line change. NO engineering /
// economic / statistical math (Charter Absolute #2) — pure state container.

import { useCallback, useSyncExternalStore } from "react";
import type { JobTimeLog } from "../../jobs/types";
import { TIMELINE_STORAGE_PREFIX } from "../constants";

type Listener = () => void;

/** The localStorage key for a job's time-log shard. Encapsulated so a future key
 *  rename touches this single line (KEEP the existing key — no migration). */
export function timeLogStorageKey(jobId: string): string {
  return `${TIMELINE_STORAGE_PREFIX}${jobId}`;
}

interface JobTimeLogShard {
  read: () => JobTimeLog | null;
  write: (next: JobTimeLog) => void;
  /** Session-only write: the log is readable but NEVER persisted (PB-04 Inc 2b). */
  writeTransient: (next: JobTimeLog) => void;
  clear: () => void;
  subscribe: (listener: Listener) => () => void;
}

/** A sentinel distinct from any possible localStorage value (string | null), so
 *  the first read always misses the cache and reconciles. */
const UNREAD = Symbol("unread");

/** Minimal shape guard — a valid time log has entries[] + lanes[]. Mirrors the
 *  guard the legacy useTimeline.loadData / timelineDepthSource.loadTimelineForJob
 *  applied, so malformed blobs read as `null` (caller falls back). */
function isJobTimeLog(v: unknown): v is JobTimeLog {
  return (
    typeof v === "object" &&
    v !== null &&
    Array.isArray((v as JobTimeLog).entries) &&
    Array.isArray((v as JobTimeLog).lanes)
  );
}

// WHY THE PLANNED-CURVE MIGRATION IS *NOT* HERE — a red-team Sev-0, and the first design of it
// was wrong in a way worth leaving written down.
//
// AFE-10 made `PlannedCurveSet` a union discriminated on `source`, and logs written before it
// carry the old shape. The obvious place to repair that is this read: one seam, every reader
// covered. It was built that way, with a comment asserting that the repair was safe under Abs-7
// because `migratePlannedCurveSet` never writes to storage.
//
// THAT WAS TRUE AND BESIDE THE POINT. `useTimeline`'s `data` is whatever this read returns, and
// every mutator on that hook spreads `data` into `persist` → `write` → `JSON.stringify` over this
// very key. So a read that nulled an unreadable curve set, followed by the user adding a LANE,
// wrote `plannedCurveSet: null` over the stored JSON. The uploaded plan — curves, filename,
// import provenance — was destroyed by an unrelated edit, silently and unrecoverably. Reproduced
// end to end in review, not theorised.
//
// So the shard now returns EXACTLY what was stored, and the migration runs at the point of USE
// (`useTimeline`), where its output reaches the chart and never reaches `write`. What round-trips
// through storage is always the original bytes. Abs-7 is satisfied by the data never being
// rewritten, not by a promise that one function in the chain does not write.

function createShard(jobId: string): JobTimeLogShard {
  const key = timeLogStorageKey(jobId);
  let cachedRaw: string | null | typeof UNREAD = UNREAD;
  let cachedValue: JobTimeLog | null = null;
  // When a write can't persist (quota exceeded / private mode), the in-memory value is AUTHORITATIVE
  // for the session — localStorage still holds the stale prior value, so read() must NOT reconcile
  // against it (that would drop the just-written log). Large demo imports (350 timeline shards) exceed
  // the ~5MB localStorage quota.
  let unpersisted = false;
  const listeners = new Set<Listener>();

  const read = (): JobTimeLog | null => {
    if (unpersisted) return cachedValue;
    let raw: string | null;
    try {
      raw = localStorage.getItem(key);
    } catch {
      raw = null; // storage blocked / SSR → behave as empty (never throws)
    }
    if (raw === cachedRaw) return cachedValue; // unchanged → stable reference
    cachedRaw = raw;
    if (!raw) {
      cachedValue = null;
      return cachedValue;
    }
    try {
      const parsed: unknown = JSON.parse(raw);
      cachedValue = isJobTimeLog(parsed) ? parsed : null;
    } catch {
      cachedValue = null; // corrupt JSON → null (caller falls back)
    }
    return cachedValue;
  };

  const notify = (): void => listeners.forEach((l) => l());

  const write = (next: JobTimeLog): void => {
    let serialized: string | null = null;
    try {
      serialized = JSON.stringify(next);
      localStorage.setItem(key, serialized);
      unpersisted = false;
    } catch {
      // Storage unavailable (private mode / quota / SSR) — keep the in-memory value AUTHORITATIVE for
      // this session (read() returns the cache without reconciling); persistence is best-effort. Large
      // demo imports (350 timeline shards) exceed the ~5MB localStorage quota.
      unpersisted = true;
    }
    // Adopt `next` as the cache with a stable reference so a subsequent read()
    // returns exactly this object (and short-circuits until localStorage changes).
    cachedRaw = serialized;
    cachedValue = next;
    notify();
  };

  // Session-only write (PB-04 Inc 2b). Corpus mode hydrates a well's time logs on open; a rich
  // well carries six of them and persisting each would refill the ~5 MB quota after a handful of
  // wells — the exact failure the corpus store exists to remove. Reuses the `unpersisted` latch
  // the quota path already established: the in-memory value is authoritative and read() does not
  // reconcile against localStorage, which here holds nothing for this job.
  const writeTransient = (next: JobTimeLog): void => {
    unpersisted = true;
    cachedValue = next;
    cachedRaw = UNREAD;
    notify();
  };

  const clear = (): void => {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore
    }
    cachedRaw = null;
    cachedValue = null;
    // Reset the unpersisted latch: the shard is now empty AND consistent with
    // localStorage, so read() must resume reconciling (a stale `unpersisted=true`
    // from an earlier quota-blocked write would otherwise pin read() to the cache).
    unpersisted = false;
    notify();
  };

  const subscribe = (listener: Listener): (() => void) => {
    listeners.add(listener);
    const onStorage = (e: StorageEvent): void => {
      if (e.key === key || e.key === null) listener(); // null key = storage.clear()
    };
    try {
      window.addEventListener("storage", onStorage);
    } catch {
      // SSR — no window; same-tab listeners still work.
    }
    return () => {
      listeners.delete(listener);
      try {
        window.removeEventListener("storage", onStorage);
      } catch {
        // ignore
      }
    };
  };

  return { read, write, writeTransient, clear, subscribe };
}

/** One shard instance per jobId — so subscribe/read are stable per job (required
 *  by useSyncExternalStore) and every reader of the same job shares one source. */
const shards = new Map<string, JobTimeLogShard>();

function shardFor(jobId: string): JobTimeLogShard {
  let shard = shards.get(jobId);
  if (!shard) {
    shard = createShard(jobId);
    shards.set(jobId, shard);
  }
  return shard;
}

/** Read a job's canonical time log (or null when absent / malformed). Returns a
 *  STABLE reference while the underlying localStorage string is unchanged. */
export function readJobTimeLog(jobId: string): JobTimeLog | null {
  if (!jobId) return null;
  return shardFor(jobId).read();
}

/** Persist a job's canonical time log (localStorage shard) + notify subscribers. */
/** Session-only variant of `writeJobTimeLog` — readable by `useTimeline`, never persisted.
 *  Used by corpus-mode hydration, where the time logs come from the API on demand. */
export function writeJobTimeLogTransient(
  jobId: string,
  timeLog: JobTimeLog,
): void {
  shardFor(jobId).writeTransient(timeLog);
}

export function writeJobTimeLog(jobId: string, timeLog: JobTimeLog): void {
  if (!jobId) return;
  shardFor(jobId).write(timeLog);
}

/** Subscribe to a job's time-log shard (same-tab writes + cross-tab `storage`
 *  events). Returns an unsubscribe fn. */
export function subscribeJobTimeLog(
  jobId: string,
  listener: Listener,
): () => void {
  return shardFor(jobId).subscribe(listener);
}

/** Remove a job's time-log shard (deletion cleanup — the job id is unrecoverable
 *  once its borefield/well is gone). No-op for a job with no stored time log. */
export function clearJobTimeLog(jobId: string): void {
  if (!jobId) return;
  shardFor(jobId).clear();
}

/** Remove EVERY persisted time-log shard by key prefix.
 *
 *  Time logs are sharded per job and keyed by job id, so a caller that drops the
 *  whole spine (`reloadDemoData` — padsStore/wellsStore/padJobsStore.setAll([]))
 *  loses the ids of the very shards it needs to clean up, and every one of them
 *  ORPHANS in localStorage forever. Each demo reload mints new job ids, so the
 *  orphans accumulate: reload the demo enough times and the ~5MB quota is spent
 *  entirely on time logs for jobs that no longer exist.
 *
 *  Returns the number of shards removed. Prefix-scoped — it touches nothing but
 *  `oxbo-timeline-v8:*`. */
export function clearAllJobTimeLogs(): number {
  let removed = 0;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(TIMELINE_STORAGE_PREFIX)) keys.push(k);
    }
    for (const k of keys) {
      localStorage.removeItem(k);
      removed++;
    }
  } catch {
    // Private mode / no storage — nothing persisted, nothing to clean.
  }
  // Drop the in-memory shard caches too, so a live subscriber re-reads (and gets
  // null) rather than serving the cached log of a job that was just deleted.
  for (const shard of shards.values()) shard.clear();
  shards.clear();
  return removed;
}

export interface UseJobTimeLogReturn {
  /** The job's time log, or null when none is stored yet. */
  timeLog: JobTimeLog | null;
  /** Persist a new time log for this job (writes the shard + notifies). */
  write: (next: JobTimeLog) => void;
}

/** Reactive read + write of a job's canonical time-log shard. Re-renders on any
 *  write to the SAME job (this tab or another). */
export function useJobTimeLog(jobId: string): UseJobTimeLogReturn {
  const subscribe = useCallback(
    (listener: Listener) => subscribeJobTimeLog(jobId, listener),
    [jobId],
  );
  const getSnapshot = useCallback(() => readJobTimeLog(jobId), [jobId]);
  const timeLog = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const write = useCallback(
    (next: JobTimeLog) => writeJobTimeLog(jobId, next),
    [jobId],
  );
  return { timeLog, write };
}
